// src/components/CallLog/Day10CallLog.jsx — Light Theme
// ─────────────────────────────────────────────────────────────────────────────
// Modal for logging the Day-10 call.
// Props:
//   open        — boolean, controls visibility
//   employee    — full employee object
//   onClose()   — callback to close without saving
//   onSaved()   — callback after successful save
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from "react";
import {
  Modal, DatePicker, Select, Input, Button,
  notification, Tooltip, Progress,
} from "antd";
import {
  CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined,
  SaveOutlined, PhoneOutlined, CalendarOutlined,
  QuestionCircleOutlined, UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import callService from "../../services/callService";
import { useDay10Questions } from "../../context/MasterDataContext";
import "./Day10CallLog.css";

const C = {
  blue:"#1E6FBF", orange:"#E8871A", navy:"#1B2D6B",
  green:"#16a34a", red:"#dc2626", amber:"#b45309",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0",
};

const YES_NO = ["Yes", "No", "NA"];
const CALL_STATUS_OPTS = ["Completed", "Not Reachable", "Call Later", "No Response"];

const ANSWER_META = {
  Yes: { icon: <CheckCircleOutlined  />, color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
  No:  { icon: <CloseCircleOutlined  />, color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
  NA:  { icon: <MinusCircleOutlined  />, color: "#7a96b0", bg: "#f5f8fc", border: "#dce6f0" },
};

// Rating colors: 1-2 = red, 3 = amber, 4-5 = green
const RATING_COLOR = (r) =>
  r >= 4 ? "#16a34a" : r === 3 ? "#b45309" : "#dc2626";
const RATING_BG    = (r) =>
  r >= 4 ? "#f0fdf4"  : r === 3 ? "#fffbeb"  : "#fef2f2";
const RATING_BORDER= (r) =>
  r >= 4 ? "#86efac"  : r === 3 ? "#fde68a"  : "#fca5a5";

// ─────────────────────────────────────────────────────────────────────────────
// Hard-coded answerType overrides — applied even when master_data meta is missing.
// Q3 = comfort rating 1-5, Q8 = daily-plan ability rating 1-5,
// Q10 = reporting style choice (Daily / Call by Call)
const ANSWER_TYPE_OVERRIDE: Record<string, string> = {
  q3:  'rating5',
  q8:  'rating5',
  q10: 'choice',
};
const CHOICE_OPTIONS: Record<string, string[]> = {
  q10: ['Daily', 'Call by Call'],
};
// Normalise Q10 answer to canonical form when pre-filling from DB
function normaliseQ10(v) {
  if (!v) return v;
  const l = v.trim().toLowerCase();
  if (l === 'daily') return 'Daily';
  if (l.includes('call') && l.includes('call')) return 'Call by Call';
  return v;
}

export default function Day10CallLog({ open, employee, callKey = "day10", onClose, onSaved }) {

  const rawQuestions   = useDay10Questions();   // ← live from DB via context
  // Memoize so the array reference is stable — prevents useEffect from re-running on every render
  const day10Questions = useMemo(() =>
    rawQuestions.map(q => ({
      ...q,
      answerType: ANSWER_TYPE_OVERRIDE[q.key] || q.answerType || 'yesno',
      choices:    CHOICE_OPTIONS[q.key] || q.choices || [],
    })),
    [rawQuestions]  // stable — rawQuestions only changes when master data reloads
  );
  const [callDate,     setCallDate]     = useState(null);
  const [callDuration, setCallDuration] = useState(""); // minutes
  const [callStatus,  setCallStatus]  = useState("Completed");
  const [remarks,     setRemarks]     = useState("");
  const [answers,     setAnswers]     = useState({});
  const [saving,      setSaving]      = useState(false);
  const [touched,     setTouched]     = useState(false);
  const [showRemarks, setShowRemarks] = useState(false);

  // Pre-fill from existing call data when modal opens
  useEffect(() => {
    if (!open || !employee) return;
    const calls = employee.calls || [];
    const existing = Array.isArray(calls)
      ? (calls.find((c) => c.callKey === callKey) || {})
      : (calls?.[callKey] || {});
    setCallDate(existing.callDate ? dayjs(existing.callDate) : dayjs());
    setCallDuration(existing.callDuration != null ? String(existing.callDuration) : "");
    setCallStatus(existing.status  || "Completed");
    setRemarks(existing.remarks   || "");

    const existingStatus = existing.status || "Completed";
    // Read answers from day10Response (nested object) or directly from call
    const resp = existing.day10Response || existing;
    const prefill: Record<string, string> = {};
    // Use the latest question keys — read from override map + common q1-q12
    const allKeys = ["q1","q2","q3","q4","q5","q6","q7","q8","q9","q10","q11","q12"];
    allKeys.forEach(key => {
      const aType = ANSWER_TYPE_OVERRIDE[key] || "yesno";
      let saved = resp[key] ? String(resp[key]) : "";
      // Normalise Q10 casing from DB (case-insensitive)
      if (key === "q10" && saved) saved = normaliseQ10(saved);
      // Only auto-fill NA for yesno questions when status is not Completed
      if (saved) {
        prefill[key] = saved;
      } else if (aType === "yesno" && existingStatus !== "Completed") {
        prefill[key] = "NA";
      } else {
        prefill[key] = "";
      }
    });
    setAnswers(prefill);
    setTouched(false);
    setShowRemarks(!!(existing.remarks));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee, callKey]);  // ← IMPORTANT: exclude day10Questions to prevent answer reset on re-render

  // ── Derived stats ──────────────────────────────────────────────
  const answeredCount = Object.values(answers).filter(Boolean).length;
  const yesCount      = Object.values(answers).filter((v) => v === "Yes").length;
  const noCount       = Object.values(answers).filter((v) => v === "No").length;
  const completePct   = day10Questions.length
    ? Math.round((answeredCount / day10Questions.length) * 100)
    : 0;
  const allAnswered   = day10Questions.length > 0 && answeredCount === day10Questions.length;

  function setAnswer(key, val) {
    setAnswers((prev) => ({ ...prev, [key]: val }));
  }

  // When status is not Completed → auto-fill all unanswered questions with NA
  function handleStatusChange(val) {
    setCallStatus(val);
    if (val !== "Completed") {
      setAnswers(prev => {
        const updated = { ...prev };
        // Only auto-fill NA for yesno questions; leave rating and choice blank
        ["q1","q2","q3","q4","q5","q6","q7","q8","q9","q10","q11","q12"].forEach(key => {
          const aType = ANSWER_TYPE_OVERRIDE[key] || "yesno";
          if (!updated[key] && aType === "yesno") updated[key] = "NA";
        });
        return updated;
      });
    }
  }

  async function handleSave() {
    setTouched(true);
    if (!callDate) return;

    setSaving(true);
    try {
      const callData = {
        status:   callStatus,
        callDate:     callDate.format("YYYY-MM-DD"),
        callDuration: (() => { const d=parseInt(callDuration,10); return !isNaN(d)&&d>0?d:null; })(),
        remarks,
        ...answers,
      };

      const calls = employee.calls || [];
      const existing = Array.isArray(calls)
        ? calls.find((c) => c.callKey === callKey)
        : calls?.[callKey];

      if (existing) {
        await callService.updateDayN(employee.id, callKey, callData);
      } else {
        await callService.logDayN(employee.id, callKey, callData);
      }

      notification.success({
        message:     "Day-10 Call Saved",
        description: `${employee.empName}'s Day-10 call has been recorded.`,
        placement:   "topRight",
        duration:    4,
        icon:        <CheckCircleOutlined style={{ color: C.green }} />,
      });
      onSaved?.();
    } catch (err) {
      notification.error({ message: "Save failed", description: err.message });
    } finally {
      setSaving(false);
    }
  }

  const emp = employee || {};

  // ─────────────────────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={660}
      centered
      destroyOnClose
      className="d10-modal"
      title={null}
    >
      <div className="d10-root">

        {/* ── Header ── */}
        <div className="d10-header">
          <div className="d10-header-icon"><PhoneOutlined /></div>
          <div>
            <div className="d10-header-title">Day-10 Call Log</div>
            <div className="d10-header-sub">
              <UserOutlined style={{ marginRight: 5 }} />
              {emp.empName} &nbsp;·&nbsp;
              <span className="d10-emp-code">{emp.empCode}</span> &nbsp;·&nbsp;
              {emp.division} &nbsp;·&nbsp; {emp.hq}
            </div>
          </div>
          <div className="d10-progress-ring">
            <Progress
              type="circle"
              percent={completePct}
              size={48}
              strokeColor={completePct === 100 ? C.green : C.blue}
              trailColor="rgba(255,255,255,0.2)"
              strokeWidth={8}
              format={(p) => (
                <span style={{ fontSize: 10, fontWeight: 700, color: "#fff" }}>{p}%</span>
              )}
            />
            <div className="d10-progress-label">Done</div>
          </div>
        </div>

        {/* ── Call metadata row ── */}
        <div className="d10-meta-row">
          <div className="d10-meta-field">
            <label className="d10-label">
              <CalendarOutlined style={{ marginRight: 5 }} />Call Date <span className="required-star">*</span>
            </label>
            <DatePicker
              value={callDate}
              onChange={(d) => setCallDate(d)}
              format="DD MMM YYYY"
              style={{ width: "100%", borderRadius: 9 }}
              status={touched && !callDate ? "error" : ""}
              placeholder="Select call date"
              disabledDate={(d) => d && d.isAfter(dayjs())}
            />
            {touched && !callDate && (
              <span className="d10-err">Call date is required</span>
            )}
          </div>

          {/* ── Call Duration ──────────────────────── */}
          <div className="d10-meta-field">
            <label className="d10-label" style={{ display:"flex", alignItems:"center", gap:6 }}>
              ⏱ Call Duration
              <span style={{ fontSize:11, color:"#7a96b0", fontWeight:400 }}>
                (minutes, optional)
              </span>
            </label>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <Input
                type="number" min={1} max={180}
                placeholder="e.g. 10"
                value={callDuration}
                onChange={e => {
                  const v = e.target.value;
                  if (v === "" || (Number(v) >= 1 && Number(v) <= 180))
                    setCallDuration(v);
                }}
                style={{ borderRadius:9, width:110 }}
                suffix={<span style={{ color:"#7a96b0", fontSize:11 }}>min</span>}
              />
              <div style={{ display:"flex", gap:4 }}>
                {[5,10,15,20,30].map(m => (
                  <button key={m} type="button"
                    onClick={() => setCallDuration(String(m))}
                    style={{
                      padding:"3px 9px", borderRadius:20, fontSize:11,
                      fontWeight:700, cursor:"pointer", border:"none",
                      background: callDuration===String(m) ? "#1B2D6B" : "#EAF1F8",
                      color:      callDuration===String(m) ? "#fff"    : "#1E6FBF",
                    }}>
                    {m}m
                  </button>
                ))}
              </div>
            </div>
            {callDuration && (
              <div style={{ fontSize:11, color:"#16a34a", marginTop:3, fontWeight:600 }}>
                ✓ {callDuration} minute{callDuration!=="1"?"s":""} recorded
              </div>
            )}
          </div>

          <div className="d10-meta-field">
            <label className="d10-label">Call Status</label>
            <Select
              value={callStatus}
              onChange={handleStatusChange}
              style={{ width: "100%", borderRadius: 9 }}
              options={CALL_STATUS_OPTS.map((o) => ({ value: o, label: o }))}
            />
          </div>
        </div>

        {/* ── Answer summary chips ── */}
        <div className="d10-answer-summary">
          <div className="d10-summary-chip d10-chip--yes">
            <CheckCircleOutlined /> {yesCount} Yes
          </div>
          <div className="d10-summary-chip d10-chip--no">
            <CloseCircleOutlined /> {noCount} No
          </div>
          <div className="d10-summary-chip d10-chip--na">
            <MinusCircleOutlined /> {Object.values(answers).filter((v) => v === "NA").length} NA
          </div>
          <div className="d10-summary-chip d10-chip--pending">
            <QuestionCircleOutlined /> {day10Questions.length - answeredCount} Pending
          </div>
        </div>

        {/* ── Questions list ── */}
        <div className="d10-questions-wrap">
          <div className="d10-questions-title">Structured Questions</div>

          {day10Questions.map((q, idx) => {
            const val        = answers[q.key] || "";
            const answerType = q.answerType || "yesno";
            const yesnoMeta  = val && ANSWER_META[val] ? ANSWER_META[val] : null;
            const ratingVal  = parseInt(val, 10);
            const isAnswered = !!val;

            // Border/bg colour for the row
            let rowStyle = {};
            if (answerType === "yesno"   && yesnoMeta) rowStyle = { borderLeftColor: yesnoMeta.color, background: yesnoMeta.bg };
            if (answerType === "rating5"  && ratingVal) rowStyle = { borderLeftColor: RATING_COLOR(ratingVal), background: RATING_BG(ratingVal) };
            if (answerType === "choice"   && val)       rowStyle = { borderLeftColor: "#1E6FBF", background: "#eff6ff" };

            return (
              <div
                key={q.key}
                className={`d10-question-row ${isAnswered ? "d10-q--answered" : ""} ${touched && !isAnswered ? "d10-q--unanswered" : ""}`}
                style={rowStyle}
              >
                <div className="d10-q-number">{idx + 1}</div>
                <div className="d10-q-body">
                  <div className="d10-q-text">{q.label}</div>

                  {/* ── Yes / No / NA ── */}
                  {answerType === "yesno" && (
                    <div className="d10-q-buttons">
                      {YES_NO.map((opt) => {
                        const m   = ANSWER_META[opt];
                        const sel = val === opt;
                        return (
                          <button
                            type="button"
                            key={opt}
                            className={`d10-yn-btn ${sel ? "d10-yn-btn--selected" : ""}`}
                            style={sel ? { background: m.bg, borderColor: m.border, color: m.color } : {}}
                            onClick={() => setAnswer(q.key, opt)}
                          >
                            <span className="d10-yn-icon" style={sel ? { color: m.color } : {}}>{m.icon}</span>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* ── Rating 1–5 ── */}
                  {answerType === "rating5" && (
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, flexWrap:"wrap" }}>
                      {/* Quick-select integer buttons */}
                      <div style={{ display:"flex", gap:5 }}>
                        {[1,2,3,4,5].map(n => {
                          const floatVal = parseFloat(val);
                          const sel = floatVal === n;
                          const col = RATING_COLOR(n);
                          const bg  = RATING_BG(n);
                          const bdr = RATING_BORDER(n);
                          return (
                            <button
                              key={n}
                              onClick={() => setAnswer(q.key, sel ? "" : String(n))}
                              style={{
                                width:38, height:38, borderRadius:10,
                                border: `2px solid ${sel ? col : "#dce6f0"}`,
                                background: sel ? bg : "#fafcff",
                                color: sel ? col : "#7a96b0",
                                fontWeight:800, fontSize:15,
                                cursor:"pointer", transition:"all .15s",
                                display:"flex", alignItems:"center", justifyContent:"center",
                              }}
                            >
                              {n}
                            </button>
                          );
                        })}
                      </div>
                      {/* Decimal input for point values e.g. 3.5 */}
                      <input
                        type="number" min="1" max="5" step="0.5"
                        placeholder="e.g. 3.5"
                        value={val}
                        onChange={e => {
                          const v = e.target.value;
                          const n = parseFloat(v);
                          if (v === "" || (n >= 1 && n <= 5)) setAnswer(q.key, v);
                        }}
                        style={{
                          width:72, height:38, borderRadius:10, textAlign:"center",
                          border:`2px solid ${val && !isNaN(parseFloat(val)) ? RATING_BORDER(parseFloat(val)) : "#dce6f0"}`,
                          background:"#fafcff", color:"#1a2d4a",
                          fontWeight:700, fontSize:13, outline:"none", padding:"0 6px",
                        }}
                      />
                      <div style={{ fontSize:11, color:"#7a96b0", display:"flex", gap:10, alignItems:"center" }}>
                        <span style={{ color:"#dc2626", fontWeight:600 }}>1 = Low</span>
                        <span>·</span>
                        <span style={{ color:"#16a34a", fontWeight:600 }}>5 = High</span>
                        {val && !isNaN(parseFloat(val)) && (
                          <span style={{
                            marginLeft:6, padding:"2px 10px", borderRadius:20,
                            background: RATING_BG(parseFloat(val)),
                            color: RATING_COLOR(parseFloat(val)),
                            border: `1px solid ${RATING_BORDER(parseFloat(val))}`,
                            fontWeight:700, fontSize:11,
                          }}>
                            {val}/5
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Choice (e.g. Daily / Call by Call) ── */}
                  {answerType === "choice" && (
                    <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
                      {(q.choices || ["Daily", "Call by Call"]).map(opt => {
                        const sel = val === opt;
                        return (
                          <button
                            key={opt}
                            onClick={() => setAnswer(q.key, opt)}
                            style={{
                              padding:"6px 18px", borderRadius:20,
                              border: `2px solid ${sel ? "#1B2D6B" : "#dce6f0"}`,
                              background: sel ? "#1B2D6B" : "#fafcff",
                              color: sel ? "#fff" : "#4a6580",
                              fontWeight:700, fontSize:12, cursor:"pointer",
                              transition:"all .15s",
                              display:"flex", alignItems:"center", gap:6,
                            }}
                          >
                            {sel && <CheckCircleOutlined style={{ fontSize:12 }}/>}
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                </div>

                {/* Answer badge — shows selected answer clearly */}
                {isAnswered && (
                  <div className="d10-q-badge" style={{
                    display:"flex", alignItems:"center", gap:5,
                    padding:"4px 10px", borderRadius:20, flexShrink:0,
                    fontWeight:700, fontSize:12,
                    color: answerType === "yesno"
                      ? (yesnoMeta?.color || "#7a96b0")
                      : answerType === "rating5"
                        ? RATING_COLOR(ratingVal)
                        : "#1B2D6B",
                    background: answerType === "yesno"
                      ? (yesnoMeta?.bg || "#f5f8fc")
                      : answerType === "rating5"
                        ? RATING_BG(ratingVal)
                        : "#e0e7ff",
                    border: `1px solid ${
                      answerType === "yesno"
                        ? (yesnoMeta?.border || "#dce6f0")
                        : answerType === "rating5"
                          ? RATING_BORDER(ratingVal)
                          : "#c7d2fe"
                    }`,
                  }}>
                    {answerType === "yesno"   && <>{yesnoMeta?.icon} {val}</>}
                    {answerType === "rating5" && <>{val} / 5</>}
                    {answerType === "choice"  && <><CheckCircleOutlined style={{fontSize:11}}/> {val}</>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Remarks — collapsible ── */}
        <div className="d10-remarks-wrap">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <label className="d10-label">Remarks / Observations</label>
            <button
              onClick={() => setShowRemarks(r => !r)}
              style={{ fontSize:11, color:C.blue, background:"none", border:"none",
                cursor:"pointer", padding:"0 2px", fontWeight:600 }}>
              {showRemarks ? "Hide ▲" : "Add ▼"}
            </button>
          </div>
          {showRemarks && (
            <Input.TextArea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              placeholder="Any issues, follow-ups, or important notes from the call…"
              style={{ borderRadius: 8, resize: "none" }}
            />
          )}
        </div>

        {/* ── Footer ── */}
        <div className="d10-footer">
          <div className="d10-footer-hint">
            {allAnswered
              ? <span style={{ color: C.green }}><CheckCircleOutlined /> All {day10Questions.length} questions answered</span>
              : <span style={{ color: C.amber }}><QuestionCircleOutlined /> {day10Questions.length - answeredCount} question{day10Questions.length - answeredCount !== 1 ? "s" : ""} unanswered</span>
            }
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button onClick={onClose} style={{ borderRadius: 9 }}>Cancel</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
              style={{ borderRadius: 9, background: C.blue, minWidth: 140, fontWeight: 600 }}            >
              {saving ? "Saving…" : "Save Day-10 Call"}
            </Button>
          </div>
        </div>

      </div>
    </Modal>
  );
}